import React, { useState, useMemo } from "react";
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { ProFilter } from '@bedrock/eed-react';
import { Space, Form, Input } from '@bedrock/components';
import { debounce } from 'lodash';
import { useDemandOptions } from "#features/useDemandOptions";
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { localPx2rem } from '#utils';

/** 行标识：有 id 为已存记录（软删），仅 key 为本地新建（硬删） */
const matchRow = (a: any, b: any) =>
    (a?.id != null && a.id === b?.id) || (a?.key != null && a.key === b?.key);

export const useFilterValues = (props: any) => {
    const { optionsMap } = useDemandOptions({
        keys: ['kol_platform'],
    });
    const [sourceData, setSourceData] = useControllableValue(props, {
        defaultValue: [],
    });

    const [filterValues, setFilterValues] = useState({
        author_id: '',
        kol_platform: [] as any[],
    });

    // 有效数据：未软删的行，为唯一“可编辑列表”来源
    const effectiveData = useMemo(() => {
        const list = sourceData?.filter((n) => !n.delete) ?? [];
        return list.map((n, index) => ({ ...n, fakeKey: index }));
    }, [sourceData]);

    const deleteList = useMemo(
        () => sourceData?.filter((n) => n.delete) ?? [],
        [sourceData],
    );

    // 1. 前端筛选：在有效数据上按作者/平台过滤
    const displayData = useMemo(() => {
        let list = effectiveData;
        if (filterValues.author_id) {
            const searchText = filterValues.author_id.trim();
            list = list.filter(
                (item) =>
                    (item.kol_code && item.kol_code === searchText) ||
                    (item.kol_name &&
                        item.kol_name.toLowerCase().includes(searchText.toLowerCase())),
            );
        }
        if (filterValues.kol_platform?.length) {
            const platforms = filterValues.kol_platform.map((p) => String(p));
            list = list.filter((item) =>
                platforms.includes(String(item.platform)),
            );
        }
        return list;
    }, [effectiveData, filterValues]);

    // 2. 表格展示用：无数据时占位一条空行（新增场景）
    const showValue = useMemo(() => {
        if (!displayData?.length && !effectiveData?.length) {
            return [{ key: Date.now() }];
        }
        return displayData;
    }, [displayData, effectiveData]);

    // 用“当前展示/编辑后的列表”按 id/key 合并回有效数据，避免筛选时丢行
    const setState = useMemoizedFn((value: any[] = []) => {
        const merged = effectiveData.map((e) => {
            const v = value.find((x) => matchRow(x, e));
            return v !== undefined ? { ...e, ...v } : e;
        });
        const append = value.filter(
            (v) => !effectiveData.some((e) => matchRow(e, v)),
        );
        const newEffective = [...merged, ...append];
        setSourceData([...newEffective, ...deleteList]);
    });

    // 新增一条空行：只往有效数据末尾追加，再写回 source
    const addRow = useMemoizedFn(() => {
        setSourceData([...effectiveData, { key: Date.now() }, ...deleteList]);
    });

    // 删除：有 id 软删，无 id 从有效数据中移除
    const deleteRow = useMemoizedFn((record: any) => {
        if (record?.id) {
            const idx = sourceData.findIndex((n) => n.id === record.id);
            if (idx < 0) return;
            const next = [...sourceData];
            next[idx] = { ...next[idx], delete: true };
            setSourceData(next);
        } else {
            const rest = sourceData.filter((n) => n.key !== record?.key);
            setSourceData(rest);
        }
    });

    const onFinish = useMemoizedFn((values: any) => {
        setFilterValues((prev) => ({ ...prev, ...values }));
    });

    const handleReset = useMemoizedFn(() => {
        setFilterValues({ author_id: '', kol_platform: [] });
    });

    return {
        filterValues,
        showValue,
        state: sourceData,
        originalShowValue: effectiveData,
        setState,
        addRow,
        deleteRow,
        setSourceData,
        onFinish,
        handleReset,
        optionsMap,
    };
}

export const useColumns = (view: boolean) => {
    const fakeKey = {
        title: '#',
        dataIndex: 'fakeKey',
        align: 'left',
        fixed: 'left',
        width: '56px',
        render: (text, record, index) => {
            return (
                <span style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: localPx2rem(14) }}>{index+1}</span>
            )
        }
    }

    const createdAt =  {
        title: '加热链接上传时间',
        dataIndex: 'created_at',
        width: view ? '160px' : '180px',
        align: 'left',
        render: (text) => <div>{text || "--"}</div>
    }

    return {
        fakeKey,
        createdAt,
    }
}

export const FilterForm = ({ onFinish, handleReset, filterValues, optionsMap }: { onFinish: (values: any) => void, handleReset: () => void, filterValues: any, optionsMap: any }) => {
    return (
        <div className='m-b-12'>
            <ProFilter showSearchButton={false} disableAddButton fieldOptions={[
                {
                    name: '平台',
                    value: "fileType",
                    showCondition: false,
                    type: FilterType.SELECT_MULTIPLE,
                    filterComponentProps: {
                        options: optionsMap?.['kol_platform']?.options || []
                    },
                },
            ]} showCondition={false} pin onSearch={(rest)=> {
                onFinish({kol_platform: rest?.[0]?.filterValue})
            }} onReset={handleReset}>
                <Space size={12}>
                    <Form.Item name="author_id" noStyle>
                        <Input.Search onChange={ debounce((e)=>{
                            onFinish({...filterValues, author_id: e.target.value});
                        }, 300) } placeholder='请输入作者昵称/ID' style={{ width: 200 }} />
                    </Form.Item>
                    <ProFilter.FilterGroup  />
                </Space>
            </ProFilter>
        </div>
    )
}